import {useCallback, useEffect, useMemo, useRef} from 'react';

type WindowEventHandler<K extends keyof WindowEventMap> = (
  this: Window,
  ev: WindowEventMap[K]
) => void;

interface RegistrationHandle<K extends keyof WindowEventMap> {
  register: (handler: WindowEventHandler<K>) => void;
  unregister: () => void;
}

/**
 * Utility hook to register a single window event listener.
 *
 * The handler will automatically get unregistered on unmount, or when
 * registering a new handler.
 *
 * You can either pass in the event handler directly when calling the hook, or
 * use the addListener / removeLister handle that is returned.
 */
export function useWindowEventListener<K extends keyof WindowEventMap>(
  eventType: K,
  eventHandler?: WindowEventHandler<K>
): RegistrationHandle<K> {
  const activeHandler = useRef<WindowEventHandler<K> | null>(null);
  const unregister = useCallback(() => {
    if (activeHandler.current) {
      window.removeEventListener(eventType, activeHandler.current);
      activeHandler.current = null;
    }
  }, [eventType]);

  const register = useCallback(
    (handler: WindowEventHandler<K>) => {
      if (activeHandler.current !== handler) {
        unregister();
        window.addEventListener(eventType, handler);
        activeHandler.current = handler;
      }
    },
    [eventType, unregister]
  );

  useEffect(() => {
    if (eventHandler) {
      register(eventHandler);
    }
  }, [register, eventHandler]);

  useEffect(() => unregister, [unregister]);

  return useMemo(() => ({register, unregister}), [register, unregister]);
}
